import type { MouseEventHandler, ReactNode } from 'react';
import { Box, type SxProps, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import { EditorView, type EditorViewProps } from '../../../components/BlockNote/EditorView';

export interface NoteSectionHeaderProps {
  name: string;
  headerLeading?: ReactNode;
  headerAfterTitle?: ReactNode;
  headerTrailing?: ReactNode;
}

/**
 * The section title row: leading slot, title, after-title slot, trailing slot,
 * over the purple rule that separates a section's heading from its body.
 *
 * Extracted so non-editor section bodies (the chart block) sit under exactly
 * the same heading as an ordinary prose section, rather than a copy of it.
 */
export function NoteSectionHeader({
  name,
  headerLeading,
  headerAfterTitle,
  headerTrailing,
}: NoteSectionHeaderProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        pb: 1.5,
        borderBottom: '1px solid #E6E1FF',
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        flex: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          minWidth: 0,
          overflow: 'hidden',
          flex: 1,
        }}
      >
        {headerLeading}
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontSize: { xs: '18px', sm: '20px', md: '24px' },
            fontWeight: 700,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {name}
        </Typography>
        {headerAfterTitle}
      </Box>
      {headerTrailing}
    </Box>
  );
}

export interface NoteSectionPresentationProps {
  name: string;
  markdown: string;
  /** Controls editing chrome and the space reserved for it. */
  editable: boolean;
  /** Controls EditorView interactivity independently; defaults to `editable`. */
  editorEditable?: boolean;
  headerLeading?: ReactNode;
  headerAfterTitle?: ReactNode;
  headerTrailing?: ReactNode;
  contentFallback?: ReactNode;
  contentSx?: SxProps<Theme>;
  editorProps?: Omit<EditorViewProps, 'markdown' | 'editable'>;
  editorChildren?: ReactNode;
  onContentClick?: MouseEventHandler<HTMLDivElement>;
}

/**
 * Shared presentation layer for a note section.
 *
 * Provider-specific behavior is supplied through slots and EditorView props;
 * read-only consumers can render the same heading and BlockNote surface without
 * mounting note editing, analytics, or route-aware hooks.
 */
export function NoteSectionPresentation({
  name,
  markdown,
  editable,
  editorEditable = editable,
  headerLeading,
  headerAfterTitle,
  headerTrailing,
  contentFallback,
  contentSx,
  editorProps,
  editorChildren,
  onContentClick,
}: NoteSectionPresentationProps) {
  const {
    sx: editorSx,
    containerStyle: editorContainerStyle,
    ...restEditorProps
  } = editorProps ?? {};

  return (
    <>
      <NoteSectionHeader
        name={name}
        headerLeading={headerLeading}
        headerAfterTitle={headerAfterTitle}
        headerTrailing={headerTrailing}
      />

      {contentFallback ?? (
        <Box
          onClick={onContentClick}
          sx={[
            {
              position: 'relative',
              pt: editable ? '36px' : 0,
              pb: editable ? 1.5 : 0,
              px: editable ? 1.5 : 0,
              mx: editable ? -1.5 : 0,
              mt: '-1px',
              borderRadius: '12px',
              border: '1px solid transparent',
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
            },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- MUI types SxProps array elements as any
            ...(Array.isArray(contentSx) ? contentSx : contentSx ? [contentSx] : []),
          ]}
        >
          <EditorView
            {...restEditorProps}
            markdown={markdown}
            editable={editorEditable}
            sx={editorSx}
            containerStyle={{
              marginLeft: '-2rem',
              marginRight: '-2rem',
              ...editorContainerStyle,
            }}
          >
            {editorChildren}
          </EditorView>
        </Box>
      )}
    </>
  );
}
